import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AdminLayout } from '../components/layout/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { PageHeader, ResponsiveTable } from '../components/ui/responsive-table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { MediaPicker } from '../components/forms/MediaPicker';
import { PriceOptionsEditor } from '../components/forms/PriceOptionsEditor';
import {
  useMassages,
  useCreateMassage,
  useUpdateMassage,
  useDeleteMassage,
} from '../hooks/useMassages';
import { useMassageCategories } from '../hooks/useServiceCategories';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { generateSlug } from '../lib/utils';
import type { Massage } from '../types/entities';

const massageSchema = z.object({
  title: z.string().min(3, 'Titre trop court (min 3 caractères)'),
  slug: z.string().min(1, 'Slug requis'),
  summary: z.string().min(10, 'Résumé trop court (min 10 caractères)').max(200, 'Résumé trop long (max 200 caractères)'),
  description: z.string().min(50, 'Description trop courte (min 50 caractères)'),
  imageUrl: z.string().nullable(),
  price: z.number().positive('Le prix doit être positif').optional(),
  duration: z.number().positive('La durée doit être positive').optional(),
  priceOptions: z.string().nullable().optional(),
  categoryId: z.string().nullable().optional(),
  benefits: z.string().nullable().optional(),
  origin: z.string().nullable().optional(),
});

type MassageFormData = z.infer<typeof massageSchema>;

export default function Massages() {
  const { data: massages, isLoading } = useMassages();
  const { data: categories } = useMassageCategories();
  const createMassage = useCreateMassage();
  const updateMassage = useUpdateMassage();
  const deleteMassage = useDeleteMassage();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMassage, setEditingMassage] = useState<Massage | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<MassageFormData>({
    resolver: zodResolver(massageSchema),
    defaultValues: {
      title: '',
      slug: '',
      summary: '',
      description: '',
      imageUrl: null,
      price: undefined,
      duration: undefined,
      priceOptions: null,
      categoryId: null,
      benefits: null,
      origin: null,
    },
  });

  const imageUrl = watch('imageUrl');
  const priceOptions = watch('priceOptions');

  const handleOpenCreate = () => {
    reset({
      title: '',
      slug: '',
      summary: '',
      description: '',
      imageUrl: null,
      price: undefined,
      duration: undefined,
      priceOptions: null,
      categoryId: null,
      benefits: null,
      origin: null,
    });
    setEditingMassage(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (massage: Massage) => {
    reset({
      title: massage.title,
      slug: massage.slug,
      summary: massage.summary,
      description: massage.description,
      imageUrl: massage.imageUrl,
      price: massage.price ?? undefined,
      duration: massage.duration ?? undefined,
      priceOptions: massage.priceOptions,
      categoryId: massage.categoryId,
      benefits: massage.benefits,
      origin: massage.origin,
    });
    setEditingMassage(massage);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    await deleteMassage.mutateAsync(id);
    setDeleteConfirm(null);
  };

  const onSubmit = async (data: MassageFormData) => {
    const cleaned = {
      ...data,
      price: data.price || null,
      duration: data.duration || null,
    };
    if (editingMassage) {
      await updateMassage.mutateAsync({ id: editingMassage.id, data: cleaned });
    } else {
      await createMassage.mutateAsync(cleaned);
    }
    setIsModalOpen(false);
  };

  // Auto-generate slug from title
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setValue('title', newTitle);
    if (!editingMassage) {
      setValue('slug', generateSlug(newTitle));
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <PageHeader
          title="Massages"
          description="Gérez vos prestations de massage"
          action={
            <Button onClick={handleOpenCreate} className="gap-2 w-full sm:w-auto">
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Nouveau massage</span>
              <span className="sm:hidden">Nouveau</span>
            </Button>
          }
        />

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {/* Mobile/Tablet: Card Layout */}
            <div className="lg:hidden space-y-4">
              {massages?.map((massage) => (
                <div
                  key={massage.id}
                  className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
                >
                  <div className="p-4">
                    <div className="flex gap-4">
                      {/* Image */}
                      {massage.imageUrl ? (
                        <img
                          src={massage.imageUrl}
                          alt={massage.title}
                          className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-gray-100 rounded-lg flex-shrink-0" />
                      )}

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {massage.title}
                        </h3>
                        <p className="text-sm text-gray-500 truncate">{massage.slug}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {massage.duration && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {massage.duration} min
                            </span>
                          )}
                          {massage.price && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {massage.price}€
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-100">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenEdit(massage)}
                        className="flex-1 sm:flex-none"
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Modifier
                      </Button>
                      {deleteConfirm === massage.id ? (
                        <>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDelete(massage.id)}
                          >
                            Confirmer
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteConfirm(null)}
                          >
                            Annuler
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteConfirm(massage.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: Table Layout */}
            <div className="hidden lg:block">
              <ResponsiveTable>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Image
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Titre
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Durée
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Prix
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {massages?.map((massage) => (
                      <tr key={massage.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          {massage.imageUrl ? (
                            <img
                              src={massage.imageUrl}
                              alt={massage.title}
                              className="w-16 h-16 object-cover rounded"
                            />
                          ) : (
                            <div className="w-16 h-16 bg-gray-100 rounded" />
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {massage.title}
                          </div>
                          <div className="text-sm text-gray-500">{massage.slug}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {massage.duration ? `${massage.duration} min` : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {massage.price ? `${massage.price}€` : '-'}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEdit(massage)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {deleteConfirm === massage.id ? (
                            <div className="inline-flex gap-1">
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDelete(massage.id)}
                              >
                                Confirmer
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteConfirm(null)}
                              >
                                Annuler
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteConfirm(massage.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ResponsiveTable>
            </div>
          </>
        )}
      </div>

      {/* Modal Form */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b">
              <h2 className="text-lg sm:text-xl font-semibold">
                {editingMassage ? 'Modifier le massage' : 'Nouveau massage'}
              </h2>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-4 sm:p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Titre *</Label>
                <Input
                  id="title"
                  {...register('title')}
                  onChange={handleTitleChange}
                  className={errors.title ? 'border-red-500' : ''}
                />
                {errors.title && (
                  <p className="text-sm text-red-600">{errors.title.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">Slug *</Label>
                <Input
                  id="slug"
                  {...register('slug')}
                  className={errors.slug ? 'border-red-500' : ''}
                />
                {errors.slug && (
                  <p className="text-sm text-red-600">{errors.slug.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="summary">Résumé *</Label>
                <Textarea
                  id="summary"
                  {...register('summary')}
                  rows={2}
                  maxLength={200}
                  className={errors.summary ? 'border-red-500' : ''}
                />
                {errors.summary && (
                  <p className="text-sm text-red-600">{errors.summary.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  {...register('description')}
                  rows={4}
                  className={errors.description ? 'border-red-500' : ''}
                />
                {errors.description && (
                  <p className="text-sm text-red-600">{errors.description.message}</p>
                )}
              </div>

              <MediaPicker
                value={imageUrl}
                onChange={(url) => setValue('imageUrl', url)}
                label="Image"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="duration">Durée (minutes)</Label>
                  <Input
                    id="duration"
                    type="number"
                    {...register('duration', { setValueAs: (v) => (v === '' || v == null) ? undefined : Number(v) })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="price">Prix (€)</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    {...register('price', { setValueAs: (v) => (v === '' || v == null) ? undefined : Number(v) })}
                  />
                </div>
              </div>

              <PriceOptionsEditor
                value={priceOptions || null}
                onChange={(value) => setValue('priceOptions', value)}
              />

              <div className="space-y-2">
                <Label htmlFor="category">Catégorie</Label>
                <Select
                  value={watch('categoryId') || ''}
                  onValueChange={(value) => setValue('categoryId', value || null)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez une catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories?.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="origin">Origine</Label>
                <Input
                  id="origin"
                  {...register('origin')}
                  placeholder="Ex: Japon, Inde, Bali..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="benefits">Bénéfices</Label>
                <Textarea
                  id="benefits"
                  {...register('benefits')}
                  rows={3}
                  placeholder="Un bénéfice par ligne"
                />
                <p className="text-xs text-gray-500">
                  Entrez un bénéfice par ligne. Sera affiché sous forme de liste à puces.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                >
                  Annuler
                </Button>
                <Button type="submit">
                  {editingMassage ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
